// src/utils/schoolLockerOrderPdf.ts
// -----------------------------------------------------------------------------
// Generates the School Locker "ORDER COPY" PDF with jsPDF and opens it in a new
// tab for printing. This REPLACES the old HTML print sheet (printChild in
// PrintButton.jsx) for School Locker orders, so the printout now matches the
// look of the other uniform printouts (uniformAuthorityPdf.ts).
//
// IMPORTANT — this is NOT an authority to purchase. The School Locker order has
// already been placed electronically (useSchoolLockerSubmission.ts sends its own
// payload); this sheet is a COPY of that placed order for the shelter/responder
// records, carrying the School Locker Reference (OSD code) once School Locker
// replies. The heading + banner say so explicitly.
//
// One call = one child's order (the caller prints the selected child).

import { customUniformRowCounts, UNIFORM_ITEMS, WINTER_ITEMS } from "./formConstants";
import { logger } from "./logger";

type RGB = [number, number, number];

interface UniItem {
  qty?: number | string;
  cost?: number | string;
  sku?: string;
  size?: string;
}
interface CustomItem {
  name?: string;
  desc?: string;
  sku?: string;
  size?: string;
  qty?: number | string;
  cost?: number | string;
}
export interface SchoolLockerOrderChild {
  items?: Record<string, UniItem>;
  customItems?: CustomItem[];
  osdCode?: string | null;
  gender?: string;
  yearLevel?: string;
  schoolName?: string;
  email?: string; // notification email (School Locker SMS/email pickup)
  mobile?: string; // notification SMS
}
export interface SchoolLockerOrderInput {
  child: SchoolLockerOrderChild;
  childIndex: number; // 0-based → "Child N"
  shelterName?: string;
  refNo?: string; // Zephyr reference (e.g. 2026-00278)
  shelterCode?: string;
  shelterRef?: string;
  reqDate?: string;
  totalChildren?: number;
}

function money(n: number): string {
  return `$${(Number(n) || 0).toLocaleString("en-AU", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function fmtDate(reqDate: string | undefined): string {
  if (!reqDate) return "";
  const d = new Date(reqDate);
  if (Number.isNaN(d.getTime())) return String(reqDate).slice(0, 10);
  return (
    `${String(d.getDate()).padStart(2, "0")}/` +
    `${String(d.getMonth() + 1).padStart(2, "0")}/` +
    `${d.getFullYear()}`
  );
}

function waitForJsPDF(): Promise<boolean> {
  return new Promise((resolve) => {
    let n = 0;
    const tick = () => {
      if (window.jspdf?.jsPDF) return resolve(true);
      if (++n > 50) return resolve(false);
      setTimeout(tick, 100);
    };
    tick();
  });
}

// The ordered lines (qty > 0) for one child's School Locker order — the fixed
// UNIFORM/WINTER items followed by the named custom rows (identity + qty + cost).
interface OrderLine {
  name: string;
  sku: string;
  size: string;
  cost: number;
  qty: number;
}
export function schoolLockerOrderLines(child: SchoolLockerOrderChild): OrderLine[] {
  const out: OrderLine[] = [];
  const map = child.items || {};
  for (const k of [...UNIFORM_ITEMS, ...WINTER_ITEMS]) {
    const it = map[k];
    const qty = Number(it?.qty) || 0;
    if (qty > 0) {
      out.push({
        name: k,
        sku: String(it?.sku || ""),
        size: String(it?.size || ""),
        cost: Number(it?.cost) || 0,
        qty,
      });
    }
  }
  for (const it of child.customItems || []) {
    if (!customUniformRowCounts(it)) continue;
    out.push({
      name: it.name || it.desc || "Custom item",
      sku: String(it.sku || ""),
      size: String(it.size || ""),
      cost: Number(it.cost) || 0,
      qty: Number(it.qty) || 0,
    });
  }
  return out;
}

// Build the School Locker order-copy PDF for one child and return its blob.
export async function generateSchoolLockerOrderPdf(
  input: SchoolLockerOrderInput
): Promise<Blob | null> {
  if (!(await waitForJsPDF())) {
    logger.warn("jsPDF not loaded");
    return null;
  }
  const JsPDF = window.jspdf!.jsPDF;
  const doc = new JsPDF({ unit: "mm", format: "a4" });

  const ink: RGB = [26, 26, 26];
  const blue: RGB = [0, 90, 135]; // the School Locker sheet's table blue
  const lineC: RGB = [200, 200, 200];
  const labelBg: RGB = [240, 240, 240];
  const white: RGB = [255, 255, 255];
  const warnFill: RGB = [255, 244, 229];
  const warnBorder: RGB = [245, 166, 35];
  const warnText: RGB = [140, 90, 0];

  const fill = (c: RGB) => doc.setFillColor(c[0], c[1], c[2]);
  const draw = (c: RGB) => doc.setDrawColor(c[0], c[1], c[2]);
  const ink0 = (c: RGB) => doc.setTextColor(c[0], c[1], c[2]);

  const pageW = 210;
  const margin = 14;
  const contentW = pageW - margin * 2;

  const child: SchoolLockerOrderChild = input.child || {};
  const lines = schoolLockerOrderLines(child);
  const grand = lines.reduce((s, l) => s + l.cost * l.qty, 0);

  let y = 14;

  // Title
  doc.setFont("helvetica", "bold");
  doc.setFontSize(15);
  ink0(blue);
  doc.text("SCHOOL LOCKER ORDER — COPY", pageW / 2, y, { align: "center" });
  y += 7;

  // "NOT an authority" banner — bordered amber box, prominent.
  const bannerLines = doc.splitTextToSize(
    "This is a COPY of an order already placed with School Locker — NOT an authority to purchase.",
    contentW - 6
  );
  const bannerH = bannerLines.length * 4.4 + 4;
  fill(warnFill);
  draw(warnBorder);
  doc.rect(margin, y, contentW, bannerH, "FD");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9.5);
  ink0(warnText);
  doc.text(bannerLines, margin + 3, y + 5);
  y += bannerH + 6;

  // Child heading
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  ink0(ink);
  doc.text(`Child ${input.childIndex + 1}`, margin, y);
  const childBits = [
    child.gender ? `Gender: ${child.gender}` : "",
    child.yearLevel ? `Year Level: ${child.yearLevel}` : "",
    child.schoolName ? `School: ${String(child.schoolName).replace(/\s+—\s+/g, ", ")}` : "",
  ].filter(Boolean);
  if (childBits.length) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    ink0([90, 90, 90]);
    const sub = doc.splitTextToSize(childBits.join("    "), contentW - 22);
    doc.text(sub, margin + 22, y);
  }
  y += 7;

  // Header detail rows (label box + value box)
  const labelW = 72;
  const rowH = 8;
  const headerRow = (label: string, value: string) => {
    fill(labelBg);
    draw(lineC);
    doc.rect(margin, y, labelW, rowH, "FD");
    doc.rect(margin + labelW, y, contentW - labelW, rowH);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8.5);
    ink0(ink);
    doc.text(label, margin + 2.5, y + rowH / 2 + 1.4);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.text(String(value || ""), margin + labelW + 3, y + rowH / 2 + 1.4);
    y += rowH;
  };

  headerRow("Request Date", fmtDate(input.reqDate));
  headerRow("DFV Service/Org. Code", input.shelterCode || "");
  headerRow("DFV Service/Org. Name", input.shelterName || "");
  headerRow("DFV Service/Org. Reference", input.shelterRef || input.refNo || "");
  headerRow("DFV Service/Org. Notification Email", child.email || "");
  headerRow("DFV Service/Org. Notification SMS", child.mobile || "");
  headerRow("Zephyr Reference No.", input.refNo || "");
  // School Locker Reference (OSD). Show the code when present; otherwise flag it
  // as pending so the sheet reads "awaiting", not silently blank.
  headerRow(
    "School Locker Reference",
    child.osdCode ? String(child.osdCode) : "Awaiting School Locker confirmation"
  );
  y += 6;

  // Uniform table: ITEM | SKU | SIZE | COST | QTY | $ TOTAL
  const wTot = 26;
  const wQty = 15;
  const wCost = 24;
  const wSize = 22;
  const wSku = 26;
  const xItem = margin;
  const xSku = margin + contentW - wTot - wQty - wCost - wSize - wSku;
  const xSize = xSku + wSku;
  const xCost = xSize + wSize;
  const xQty = xCost + wCost;
  const xTot = xQty + wQty;
  const tRowH = 6.8;

  const headerCell = (text: string, x: number, w: number, align: "left" | "right" | "center") => {
    const tx = align === "right" ? x + w - 2 : align === "center" ? x + w / 2 : x + 2;
    doc.text(text, tx, y + 4.5, align === "left" ? undefined : { align });
  };

  fill(blue);
  doc.rect(margin, y, contentW, tRowH, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  ink0(white);
  headerCell("UNIFORM Item", xItem, xSku - xItem, "left");
  headerCell("SKU", xSku, wSku, "left");
  headerCell("Size", xSize, wSize, "left");
  headerCell("Cost", xCost, wCost, "right");
  headerCell("Qty", xQty, wQty, "center");
  headerCell("$ Total", xTot, wTot, "right");
  y += tRowH;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  ink0(ink);
  for (const l of lines) {
    const sub = l.cost * l.qty;
    draw(lineC);
    doc.rect(margin, y, contentW, tRowH);
    doc.text(String(l.name), xItem + 2, y + 4.5);
    doc.text(l.sku, xSku + 2, y + 4.5);
    doc.text(l.size, xSize + 2, y + 4.5);
    doc.text(l.cost > 0 ? money(l.cost) : "", xCost + wCost - 2, y + 4.5, { align: "right" });
    doc.text(String(l.qty), xQty + wQty / 2, y + 4.5, { align: "center" });
    doc.text(sub > 0 ? money(sub) : "-", xTot + wTot - 2, y + 4.5, { align: "right" });
    y += tRowH;
  }

  // TOTAL COST row
  fill([240, 245, 248]);
  doc.rect(margin, y, contentW, tRowH, "F");
  draw(blue);
  doc.setLineWidth(0.4);
  doc.line(margin, y, margin + contentW, y);
  doc.setLineWidth(0.2);
  doc.setFont("helvetica", "bold");
  ink0(ink);
  doc.text("TOTAL COST", xItem + 2, y + 4.5);
  doc.text(money(grand), xTot + wTot - 2, y + 4.5, { align: "right" });
  y += tRowH + 6;

  // Footer note
  doc.setFont("helvetica", "italic");
  doc.setFontSize(8);
  ink0([110, 110, 110]);
  const pageTotal = input.totalChildren || 1;
  doc.text(
    `Copy of order placed with School Locker.  Page ${input.childIndex + 1} of ${pageTotal}`,
    margin,
    y
  );

  return doc.output("blob");
}

// Generate + open the School Locker order copy for one child in a new tab.
// Returns false on failure so the caller can fall back to the old HTML sheet.
export async function openSchoolLockerOrderPdf(input: SchoolLockerOrderInput): Promise<boolean> {
  try {
    const blob = await generateSchoolLockerOrderPdf(input);
    if (!blob) return false;
    const objUrl = URL.createObjectURL(blob);
    window.open(objUrl, "_blank");
    setTimeout(() => URL.revokeObjectURL(objUrl), 60000);
    return true;
  } catch (e) {
    logger.warn("school locker order PDF failed:", e);
    return false;
  }
}
